import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  TemplateRef,
  viewChild,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbilityServiceSignal } from '@casl/angular';
import {
  faAngleLeft,
  faAngleRight,
  faCircleInfo,
  faLink,
  faSearchMinus,
  faSearchPlus,
  faUnlink,
} from '@fortawesome/pro-regular-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { InvoicesGateway, PdfGeneratedResponse } from '@wilson/api/gateway';
import {
  BillingSettingsService,
  formatProvisionalInvoiceNumber,
  isCompliantMode,
} from '@wilson/billing-settings';
import { ClientsV2Service } from '@wilson/clients/services';
import {
  FetchGeneralCompanySettingsIfNeeded,
  GeneralCompanySettingsState,
} from '@wilson/general-company-settings';
import {
  Client,
  GeneralCompanySettings,
  ListRowOfAttachmentWithMultipleShiftIds,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import {
  InvoiceAttachment,
  InvoiceForm,
  InvoiceFormValue,
  InvoicePreviewResponse,
  InvoiceStatus,
  InvoiceTotals,
  PersistableInvoicePayload,
  PreviewInvoiceCalculatedItem,
} from '@wilson/invoicing/interfaces';
import { InvoicingDataState } from '@wilson/invoicing/state';
import { Currency, CurrencySymbol } from '@wilson/localization';
import { EventCategory } from '@wilson/matomo';
import { base64ToBlob, DateTimeFormat } from '@wilson/utils';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  ModalButtonOptions,
  NzModalRef,
  NzModalService,
} from 'ng-zorro-antd/modal';
import { FileSaverService } from 'ngx-filesaver';
import { MatomoTracker } from 'ngx-matomo-client';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { transformPreviewToPersistPayload } from '../../mappers/invoice-preview.mapper';
import { InvoiceAttachmentsHelperService } from '../../services/invoice-attachments-helper.service';
import { InvoiceFormHelperService } from '../../services/invoice-form-helper.service';
import { InvoiceTotalsService } from '../../services/invoice-totals.service';
import { InvoicingHelperService } from '../../services/invoicing-helper.service';
import {
  CiiXmlDownloadHelperService,
  CiiXmlDownloadProgressTemplate,
} from '../invoices-overview/services/cii-xml-download-helper.service';
import { InvoicesPdfDownloadHelperService } from '../invoices-overview/services/invoices-pdf-download-helper.service';
import { ItemsToInvoiceComponent } from '../items-to-invoice/items-to-invoice.component';
import { EinvoiceExportComponent } from './e-invoice-export/e-invoice-export.component';
import { ManualInvoiceNumberService } from '../../services/manual-invoice-number.service';
import { InvoiceFormService } from './invoice-form.service';
import { LexOfficeUnlinkModalComponent } from './lex-office-unlink-modal/lex-office-unlink-modal.component';

@Component({
  selector: 'wilson-invoice-form',
  templateUrl: './invoice-form.component.html',
  styleUrls: ['./invoice-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [CiiXmlDownloadHelperService, InvoicesPdfDownloadHelperService],
  host: {
    '(document:visibilitychange)': 'onVisibilityChange()',
  },
})
export class InvoiceFormComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly fileSaverService = inject(FileSaverService);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly nzModalService = inject(NzModalService);
  private readonly invoicingHelperService = inject(InvoicingHelperService);
  private readonly invoiceFormService = inject(InvoiceFormService);
  private readonly invoicesGateway = inject(InvoicesGateway);
  private readonly tracker = inject(MatomoTracker);
  private readonly billingSettingsService = inject(BillingSettingsService);
  /**
   * Provided by the invoice page. This component is the single writer of its
   * inputs; the field component only reads them.
   */
  protected readonly manualInvoiceNumber = inject(ManualInvoiceNumberService);

  /**
   * Fetched once here and passed down to `wilson-invoice-details-form`, so the
   * parent/child pair shares a single request.
   */
  protected readonly billingSetting = toSignal(
    this.billingSettingsService.get(),
    { initialValue: null },
  );
  private clientV2Service = inject(ClientsV2Service);
  protected readonly invoiceAttachmentsHelperService = inject(
    InvoiceAttachmentsHelperService,
  );
  private readonly invoicesPdfDownloadHelperService = inject(
    InvoicesPdfDownloadHelperService,
  );
  private readonly invoiceFormHelperService = inject(InvoiceFormHelperService);
  private readonly ciiXmlDownloadHelperService = inject(
    CiiXmlDownloadHelperService,
  );
  #invoiceTotalsService = inject(InvoiceTotalsService);

  private modalRef?: NzModalRef<EinvoiceExportComponent>;

  @ViewChild(ItemsToInvoiceComponent)
  private readonly itemsToInvoiceComponent?: ItemsToInvoiceComponent;

  private readonly ciiXmlDownloadProgressTemplate =
    viewChild.required<CiiXmlDownloadProgressTemplate>(
      'ciiXmlDownloadProgressTemplate',
    );

  private _invoiceForm!: InvoiceForm;

  @Input()
  set invoiceForm(form: InvoiceForm) {
    this.formValueChangesSubscription?.unsubscribe();

    this._invoiceForm = form;

    if (form) {
      this.setupPreviewSubscription();
      this.setupManualInvoiceNumberSubscription();
    }
  }

  get invoiceForm(): InvoiceForm {
    return this._invoiceForm;
  }
  @Input() invoiceTotals!: InvoiceTotals;
  @Input() canEditInvoice!: boolean;
  @Input() isSavingInvoice = false;
  @Input() invoiceActivityIds!: string[];
  @Input({ required: true })
  shiftAttachments: ListRowOfAttachmentWithMultipleShiftIds[] | null = [];
  @Input() invoiceAttachments: InvoiceAttachment[] = [];
  @Input() disablePreSelectionOfShiftReports = false;
  @Input() set initialCalculatedItems(
    items: PreviewInvoiceCalculatedItem[] | undefined,
  ) {
    if (items?.length) {
      this.cachedCalculatedItems = items;
    }
  }
  readonly invoiceId = input<string | null>(null);

  @Output() save = new EventEmitter();
  @Output() finalizeAndSave = new EventEmitter();
  @Output() editAgain = new EventEmitter();
  @Output() cancelInvoice = new EventEmitter();
  @Output() refreshShiftAttachments = new EventEmitter();
  /** Manual Mode only: persist a post-finalization number correction (AC 5). */
  @Output() saveInvoiceNumberCorrection = new EventEmitter();

  status!: string;
  InvoiceStatus = InvoiceStatus;
  client!: Client;
  faLink = faLink;
  faUnlink = faUnlink;
  lexOfficeLogo = 'assets/img/lexoffice-logo.png';

  CurrencySymbol = CurrencySymbol;
  isInvoiceInformationActive = true;
  generalSettings$!: Observable<GeneralCompanySettings>;

  isDownloadingPDF = false;
  isGeneratingPDF = false;
  readonly #pdfRequestToken = signal(0);

  pdfURL!: string;

  protected faSearchPlus = faSearchPlus;
  protected faSearchMinus = faSearchMinus;
  protected faAngleRight = faAngleRight;
  protected faAngleLeft = faAngleLeft;
  protected faCircleInfo = faCircleInfo;

  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  private formValueChangesSubscription?: Subscription;
  private manualInvoiceNumberSubscription?: Subscription;
  private cachedCalculatedItems?: PreviewInvoiceCalculatedItem[];

  get selectedCurrency(): Currency {
    return this.invoiceForm.controls.invoiceDetails.controls.currency
      .value as Currency;
  }

  get filteredStatuses(): Exclude<InvoiceStatus, InvoiceStatus.Draft>[] {
    const currentStatus = this.invoiceForm.value.status;
    const allStatuses = Object.values(InvoiceStatus).filter(
      (status): status is Exclude<InvoiceStatus, InvoiceStatus.Draft> =>
        status !== InvoiceStatus.Cancelled && status !== InvoiceStatus.Draft,
    );

    return allStatuses.filter((status) => {
      if (status === currentStatus) {
        return true;
      }

      if (status === InvoiceStatus.Sent) {
        return currentStatus === InvoiceStatus.Open;
      }

      if (status === InvoiceStatus.Dunning) {
        return (
          currentStatus === InvoiceStatus.Open ||
          currentStatus === InvoiceStatus.Overdue
        );
      }

      return true;
    });
  }
  private pdfGenerateSubscription!: Subscription;

  constructor() {
    effect(() => {
      this.manualInvoiceNumber.billingSetting.set(this.billingSetting());
      this.manualInvoiceNumber.invoiceId.set(this.invoiceId());
      this.manualInvoiceNumber.syncWithControl(
        this.invoiceForm?.controls.invoiceDetails.controls.invoiceNumber,
      );
    });
  }

  async ngOnInit(): Promise<void> {
    this.status = this.invoiceForm.value.status as InvoiceStatus;
    await this.loadClient();
    this.cdRef.markForCheck();
    this.store.dispatch(new FetchGeneralCompanySettingsIfNeeded());
    try {
      this.generatePdf();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  }

  private setupPreviewSubscription(): void {
    this.formValueChangesSubscription = this.invoiceForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        filter(() => this.invoiceForm.valid && this.invoiceForm.dirty),
        switchMap(() =>
          this.invoiceFormHelperService.fetchInvoiceTotalsAndUpdateInvoiceItems(
            this.invoiceForm,
            !this.invoiceId()
              ? this.store.selectSnapshot(
                  InvoicingDataState.selectedActivityIds,
                )
              : this.invoiceActivityIds,
            !!this.invoiceId(),
          ),
        ),
        tap((preview) => {
          this.invoiceTotals =
            this.#invoiceTotalsService.extractInvoiceTotals(preview);
          if (preview?.calculatedItems?.length) {
            this.cachedCalculatedItems = preview.calculatedItems;
          }
          this.itemsToInvoiceComponent?.markForCheck();
          this.cdRef.detectChanges();
        }),
        catchError((error) => {
          console.error(error);
          Sentry.captureException(error);
          return of(null);
        }),
      )
      .subscribe();
  }

  private setupManualInvoiceNumberSubscription(): void {
    this.manualInvoiceNumberSubscription?.unsubscribe();
    this.manualInvoiceNumberSubscription =
      this.invoiceForm.controls.invoiceDetails.controls.invoiceNumber.valueChanges.subscribe(
        (value) =>
          this.manualInvoiceNumber.enteredNumber.set(value?.trim() ?? ''),
      );
  }

  protected saveNumberCorrection(): void {
    this.saveInvoiceNumberCorrection.emit();
  }

  ngOnDestroy(): void {
    this.pdfGenerateSubscription?.unsubscribe();
    this.formValueChangesSubscription?.unsubscribe();
    this.manualInvoiceNumberSubscription?.unsubscribe();
    if (this.pdfURL) URL.revokeObjectURL(this.pdfURL);
  }

  onStatusChange(newStatus: InvoiceStatus): void {
    this.invoiceForm.patchValue({ status: newStatus });
    this.save.emit();
    this.cdRef.detectChanges();
  }

  public async downloadInvoicePdf(): Promise<void> {
    this.tracker.trackEvent(EventCategory.Invoicing, 'invoicing-create-pdf');
    if (this.invoiceForm.valid) {
      this.isDownloadingPDF = true;
      const invoiceId = this.invoiceId();
      const status = this.invoiceForm.value.status as InvoiceStatus;
      try {
        let pdfData;
        if (invoiceId && status !== InvoiceStatus.Draft) {
          pdfData = await firstValueFrom(
            this.invoicesGateway.downloadPdf(invoiceId),
          );
        } else {
          const previewPayload =
            this.invoiceFormService.preparePreviewInvoiceDTO(
              this.invoiceForm.getRawValue() as InvoiceFormValue,
              !invoiceId
                ? this.store.selectSnapshot(
                    InvoicingDataState.selectedActivityIds,
                  )
                : this.invoiceActivityIds,
            );
          const preview = await firstValueFrom(
            this.invoicesGateway.previewInvoice(previewPayload, !!invoiceId),
          );
          const payload = this.transformPreviewWithCachedCalculatedItems(
            preview,
            !invoiceId
              ? this.store.selectSnapshot(
                  InvoicingDataState.selectedActivityIds,
                )
              : this.invoiceActivityIds,
          );

          pdfData = await firstValueFrom(
            this.invoicesGateway.generatePDF(payload),
          );
        }

        this.savePdf(pdfData);
        this.message.success(
          this.translate.instant('page.payroll.toast.success'),
        );
      } catch (error) {
        this.message.error(
          this.translate.instant('general.something_went_wrong'),
        );
        Sentry.captureException(error);
      }
      this.isDownloadingPDF = false;
      this.cdRef.detectChanges();
    }
  }

  public async downloadMergedPdf(
    progressTemplate: TemplateRef<void>,
  ): Promise<void> {
    this.tracker.trackEvent(
      EventCategory.Invoicing,
      'invoicing-create-pdf-with-attachments',
    );
    if (this.invoiceForm.valid) {
      const shiftAttachmentIds =
        this.invoiceAttachmentsHelperService.getInvoiceAttachmentsInformation()
          .selectedAttachmentIds;

      const previewPayload = this.invoiceFormService.preparePreviewInvoiceDTO(
        this.invoiceForm.getRawValue() as InvoiceFormValue,
        !this.invoiceId()
          ? this.store.selectSnapshot(InvoicingDataState.selectedActivityIds)
          : this.invoiceActivityIds,
      );
      const preview = await firstValueFrom(
        this.invoicesGateway.previewInvoice(previewPayload, !!this.invoiceId()),
      );
      const payload = this.transformPreviewWithCachedCalculatedItems(
        preview,
        !this.invoiceId()
          ? this.store.selectSnapshot(InvoicingDataState.selectedActivityIds)
          : this.invoiceActivityIds,
      );
      payload.shiftAttachmentIds = shiftAttachmentIds;
      this.isDownloadingPDF = true;
      this.cdRef.detectChanges();
      this.invoicesPdfDownloadHelperService.downloadPDFWithAttachment({
        pdfPayload: payload,
        onComplete: () => {
          this.isDownloadingPDF = false;
          this.cdRef.detectChanges();
        },
        progressTemplate,
      });
    }
  }

  async createCsv(): Promise<void> {
    const previewPayload = this.invoiceFormService.preparePreviewInvoiceDTO(
      this.invoiceForm.getRawValue() as InvoiceFormValue,
      !this.invoiceId()
        ? this.store.selectSnapshot(InvoicingDataState.selectedActivityIds)
        : this.invoiceActivityIds,
    );
    const preview = await firstValueFrom(
      this.invoicesGateway.previewInvoice(previewPayload, !!this.invoiceId()),
    );
    const payload = this.transformPreviewWithCachedCalculatedItems(
      preview,
      !this.invoiceId()
        ? this.store.selectSnapshot(InvoicingDataState.selectedActivityIds)
        : this.invoiceActivityIds,
    );
    payload.invoiceItems = payload.invoiceItems.filter((item) => !item.deleted);

    const csv = await this.invoicingHelperService.generateCsv(
      payload.invoiceItems,
    );
    const fileName = this.invoicingHelperService.generateCsvFileName();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    this.fileSaverService.save(blob, fileName);
    this.tracker.trackEvent(EventCategory.Invoicing, 'invoicing-create-csv');
    this.message.success(this.translate.instant('page.payroll.toast.success'));
  }

  private savePdf(pdfData: PdfGeneratedResponse): void {
    const pdfBlob = base64ToBlob(
      pdfData.response,
      pdfData.meta['content-type'],
    );

    this.fileSaverService.save(pdfBlob, pdfData.meta.name);
  }

  public async generatePdf(): Promise<void> {
    this.pdfGenerateSubscription?.unsubscribe();
    this.#pdfRequestToken.update((t) => t + 1);
    const token = this.#pdfRequestToken();
    const isLatest = () => this.#pdfRequestToken() === token;
    this.isGeneratingPDF = true;
    const invoiceId = this.invoiceId();
    const status = this.invoiceForm.value.status as InvoiceStatus;
    try {
      if (invoiceId && status !== InvoiceStatus.Draft) {
        const pdfData = await firstValueFrom(
          this.invoicesGateway.downloadPdf(invoiceId),
        );
        if (!isLatest()) return;
        const pdfBlob = base64ToBlob(
          pdfData.response,
          pdfData.meta['content-type'],
        );
        if (this.pdfURL) URL.revokeObjectURL(this.pdfURL);
        this.pdfURL = URL.createObjectURL(pdfBlob);
        this.isGeneratingPDF = false;
        this.invoiceForm.reset(this.invoiceForm.getRawValue());
        this.cdRef.detectChanges();
      } else {
        const previewPayload = this.invoiceFormService.preparePreviewInvoiceDTO(
          this.invoiceForm.getRawValue() as InvoiceFormValue,
          !invoiceId
            ? this.store.selectSnapshot(InvoicingDataState.selectedActivityIds)
            : this.invoiceActivityIds,
        );
        const preview = await firstValueFrom(
          this.invoicesGateway.previewInvoice(previewPayload, !!invoiceId),
        );
        if (!isLatest()) return;
        const payload = this.transformPreviewWithCachedCalculatedItems(
          preview,
          !invoiceId
            ? this.store.selectSnapshot(InvoicingDataState.selectedActivityIds)
            : this.invoiceActivityIds,
        );

        const invoiceDate = format(
          new Date(payload.invoiceDate),
          DateTimeFormat.DatabaseDateFormat,
        );

        this.pdfGenerateSubscription = this.invoicesGateway
          .generatePDF({
            ...payload,
            invoiceDate,
          })
          .pipe(filter(isLatest))
          .subscribe({
            next: (pdfData) => {
              const pdfBlob = base64ToBlob(
                pdfData.response,
                pdfData.meta['content-type'],
              );
              if (this.pdfURL) URL.revokeObjectURL(this.pdfURL);
              this.pdfURL = URL.createObjectURL(pdfBlob);
            },
            error: () => {
              if (!isLatest()) return;
              this.message.error(
                this.translate.instant('general.something_went_wrong'),
              );
              this.isGeneratingPDF = false;
              this.cdRef.detectChanges();
            },
            complete: () => {
              if (!isLatest()) return;
              this.isGeneratingPDF = false;
              this.invoiceForm.reset(this.invoiceForm.getRawValue());
              this.cdRef.detectChanges();
            },
          });
      }
    } catch {
      if (!isLatest()) return;
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.isGeneratingPDF = false;
      this.cdRef.detectChanges();
    }
  }

  private transformPreviewWithCachedCalculatedItems(
    preview: InvoicePreviewResponse,
    activityIds: string[],
  ): PersistableInvoicePayload {
    if (preview.calculatedItems?.length) {
      this.cachedCalculatedItems = preview.calculatedItems;
    }
    const payload = transformPreviewToPersistPayload(preview, activityIds);
    if (
      !payload.calculatedItems?.length &&
      this.cachedCalculatedItems?.length
    ) {
      payload.calculatedItems = this.cachedCalculatedItems;
    }
    if (this.manualInvoiceNumber.isManualMode()) {
      payload.invoiceNumber = this.manualInvoiceNumber.enteredNumber() || null;
    } else if (
      payload.status === InvoiceStatus.Draft &&
      isCompliantMode(this.billingSetting())
    ) {
      payload.invoiceNumber =
        formatProvisionalInvoiceNumber(this.billingSetting()) ??
        payload.invoiceNumber;
    }
    return payload;
  }

  editAgainInvoice(): void {
    this.editAgain.emit();
  }

  public async saveInvoice(): Promise<void> {
    this.generatePdf();
    this.save.emit();
  }

  public async cancel(): Promise<void> {
    this.cancelInvoice.emit();
  }

  public finalizeAndSaveInvoice(): void {
    this.finalizeAndSave.emit();
  }

  async exportDatev(): Promise<void> {
    try {
      const response = await this.invoicesGateway.exportDatev(
        this.invoiceId() as string,
      );

      if (!this.downloadResponseFile(response)) {
        return;
      }

      this.message.success(
        this.translate.instant(
          'page.invoice_details.export_invoice_success_message',
        ),
      );
    } catch {
      this.message.error(this.translate.instant('general.error'));
    }
  }

  exportEinvoice(): void {
    const generalSettings = this.store.selectSnapshot(
      GeneralCompanySettingsState.generalCompanySettings,
    );

    this.modalRef = this.nzModalService.create<EinvoiceExportComponent>({
      nzTitle: `${this.translate.instant(
        'page.invoicing.export_e_invoice.modal_title',
      )}`,
      nzContent: EinvoiceExportComponent,
      nzData: { client: this.client, invoice: this.invoiceForm.value },
      nzClosable: true,
      nzCentered: true,
      nzWidth: '800px',
      nzFooter: this.buildEinvoiceFooter(generalSettings),
    });

    this.modalRef.afterClose.subscribe(() => (this.modalRef = undefined));
  }

  private async loadClient(): Promise<void> {
    const clientId = this.invoiceForm.value.customerDetails?.clientId;
    if (clientId) {
      this.client = await this.clientV2Service.getClientById(clientId);
    }
  }

  /**
   * Keeps the client fresh for the next time the export modal opens.
   * The modal itself isn't live-patched while open - the error copy
   * already tells the user to re-run the export after fixing the client.
   */
  async onVisibilityChange(): Promise<void> {
    if (document.visibilityState === 'visible') {
      await this.loadClient();
    }
  }

  private buildEinvoiceFooter(
    generalSettings: GeneralCompanySettings,
  ): ModalButtonOptions<EinvoiceExportComponent>[] {
    return [
      {
        label: this.translate.instant('general.cancel'),
        type: 'default',
        onClick: (): void => {
          this.modalRef?.close();
        },
      },
      {
        label: this.translate.instant('page.invoicing.export_e_invoice.ok'),
        type: 'primary',
        disabled:
          this.invoicingHelperService.checkClientNullParams(this.client) ||
          this.invoicingHelperService.checkInvoiceNullParams(
            this.invoiceForm.getRawValue() as InvoiceFormValue,
          ) ||
          this.invoicingHelperService.checkCompanySettingsNullParams(
            generalSettings,
          ) ||
          !this.client,
        onClick: async (
          modalInstance: EinvoiceExportComponent,
        ): Promise<void> => {
          if (modalInstance.radioValue === 'xml') {
            this.ciiXmlDownloadHelperService.downloadCiiXml({
              invoiceId: this.invoiceId() as string,
              progressTemplate: this.ciiXmlDownloadProgressTemplate(),
              onComplete: () => this.modalRef?.close(),
            });
            return; // modal closes in onComplete; no awaits here
          } else {
            const response = await this.invoicesGateway.exportPdf(
              this.invoiceId() as string,
            );
            if (!this.downloadResponseFile(response)) return;
          }
          this.modalRef?.close();
        },
      },
    ];
  }

  async exportLex(): Promise<void> {
    try {
      if (!this.invoiceForm.value.lexofficeId) {
        const formValue = await firstValueFrom(
          this.invoicesGateway.createLexOffice(this.invoiceId() as string),
        );
        this.invoiceForm.patchValue({ lexofficeId: formValue.lexofficeId });
        this.message.success(
          this.translate.instant('page.invoicing.lex_success'),
        );
        this.cdRef.detectChanges();
      }
    } catch (e: unknown) {
      const err = e as { error?: { message?: string } };
      const msg = err.error?.message ?? this.translate.instant('general.error');
      this.message.error(msg);
      Sentry.captureException(e);
    }
  }

  unlinkLexOffice(): void {
    const modalRef: NzModalRef = this.nzModalService.create({
      nzTitle: `${this.translate.instant(
        'page.invoicing.export_lex_office.modal_title',
      )}`,
      nzContent: LexOfficeUnlinkModalComponent,
      nzClosable: true,
      nzCentered: true,
      nzFooter: [
        {
          label: this.translate.instant('general.cancel'),
          type: 'default',
          onClick: (): void => {
            modalRef.close();
          },
        },
        {
          label: this.translate.instant('page.invoicing.lex_office_unlink'),
          type: 'primary',
          onClick: async (): Promise<void> => {
            this.invoiceForm.patchValue({ lexofficeId: null });
            this.save.emit();
            this.message.success(
              this.translate.instant('page.invoicing.unlink_lex_success'),
            );
            modalRef.close();
          },
        },
      ],
    });
  }

  private downloadResponseFile(response: HttpResponse<Blob>): boolean {
    const contentDisposition = response.headers.get('Content-Disposition');
    const utf8Match = contentDisposition?.match(
      /filename\*=UTF-8''([^\s;]+)/,
    )?.[1];
    const fileName = utf8Match
      ? decodeURIComponent(utf8Match)
      : contentDisposition?.match(/filename="([^"]+)"/)?.[1];

    if (!fileName) {
      Sentry.captureException(
        new Error('Missing filename in Content-Disposition header'),
      );
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      return false;
    }

    saveAs(response.body, fileName);
    return true;
  }
}
